import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getProfiles, linkCard, createProfile } from "../services/api";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import { CreditCard, Check, ChevronDown, Sparkles, UserPlus, LogIn, Plus } from "lucide-react";

export default function CardRedirectPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { appName, maxProfiles: configMaxProfiles, loginLogo, accentColor } = useAppConfig();
  const [maxProfiles, setMaxProfiles] = useState(configMaxProfiles);
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [claimMode, setClaimMode] = useState(false);
  const [welcomeMode, setWelcomeMode] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [newProfileType, setNewProfileType] = useState("personal");
  const [linking, setLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [linkError, setLinkError] = useState("");

  useEffect(() => {
    let deviceId = localStorage.getItem("bitsness_device_id");
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem("bitsness_device_id", deviceId);
    }
    axios
      .get(`/api/c/${code}?device_id=${deviceId}`)
      .then((res) => {
        if (res.data.redirect) {
          navigate(res.data.redirect, { replace: true });
        }
      })
      .catch((err) => {
        const data = err.response?.data;
        const isClaimable = data?.claimable === true;
        const token = localStorage.getItem("token");

        if (isClaimable && token) {
          enterClaimMode();
        } else if (isClaimable && !token) {
          setWelcomeMode(true);
        } else if (err.response?.status === 429) {
          setError(t("card.tooManyRequests"));
        } else {
          setError(t("card.notAvailable"));
        }
      });
  }, [code]); // eslint-disable-line

  // Check if we returned from login/register with a pending card
  useEffect(() => {
    const pendingCode = sessionStorage.getItem("pending_card_code");
    const token = localStorage.getItem("token");
    if (pendingCode === code && token && !claimMode && !linkSuccess) {
      sessionStorage.removeItem("pending_card_code");
      enterClaimMode();
    }
  }, []); // eslint-disable-line

  const enterClaimMode = () => {
    setClaimMode(true);
    getProfiles()
      .then((res) => {
        const list = res.data.profiles || [];
        setProfiles(list);
        if (res.data.max_profiles != null) setMaxProfiles(res.data.max_profiles);
        if (list.length > 0) {
          setSelectedProfileId(list[0].id);
        } else {
          // No profiles — default to create new
          setCreateNew(true);
          setNewProfileName(t("card.defaultProfileName"));
        }
      })
      .catch(() => {
        setError(t("card.notAvailable"));
      });
  };

  const canCreateNew = profiles.length < maxProfiles;

  const handleLink = async () => {
    setLinking(true);
    setLinkError("");
    try {
      let profileId = selectedProfileId;

      if (createNew) {
        const name = newProfileName.trim() || t("card.defaultProfileName");
        const createRes = await createProfile({
          profile_name: name,
          profile_type: newProfileType,
        });
        profileId = createRes.data.profile.id;
        setSelectedProfileId(profileId);
        setProfiles((prev) => [...prev, createRes.data.profile]);
      }

      if (!profileId) {
        setLinkError(t("card.selectProfile"));
        setLinking(false);
        return;
      }

      await linkCard(code, profileId);
      setLinkSuccess(true);
    } catch (err) {
      if (err.response?.status === 403 && err.response.data?.max_profiles) {
        setLinkError(t("card.maxReached").replace("{max}", err.response.data.max_profiles));
      } else {
        setLinkError(err.response?.data?.error || t("card.linkFailed"));
      }
    } finally {
      setLinking(false);
    }
  };

  const handleGoToRegister = () => {
    sessionStorage.setItem("pending_card_code", code);
    navigate("/register");
  };

  const handleGoToLogin = () => {
    sessionStorage.setItem("pending_card_code", code);
    navigate("/login");
  };

  const selectedProfile = profiles.find((p) => p.id === selectedProfileId);

  // Success state
  if (linkSuccess) {
    const linkedName = createNew
      ? (newProfileName.trim() || t("card.defaultProfileName"))
      : (selectedProfile?.profile_name || t("card.defaultProfileName"));
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{t("card.linked")}</h1>
          <p className="text-gray-500 mb-6">
            {t("card.linkedDesc").replace("{name}", linkedName)}
          </p>
          <button
            onClick={() => {
              const p = profiles.find((p) => p.id === selectedProfileId);
              navigate(`/app/profile/${p?.slug || selectedProfileId}`);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition"
          >
            {t("card.goToEditor")}
          </button>
        </div>
      </div>
    );
  }

  // Welcome mode — unlinked card, user not logged in
  if (welcomeMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4">
        <div className="w-full max-w-sm text-center">
          {loginLogo ? (
            <img src={loginLogo} alt={appName} className="mx-auto mb-5 h-28 w-28 object-contain" />
          ) : (
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("card.welcomeTitle")}</h1>
          <p className="text-gray-500 mb-1">{t("card.welcomeSubtitle")}</p>
          <p className="text-sm text-gray-400 mb-8">
            {t("card.welcomeCode").replace("{code}", code.toUpperCase())}
          </p>

          <div className="space-y-3">
            <button
              onClick={handleGoToRegister}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-semibold shadow-md hover:opacity-90 transition"
              style={{ backgroundColor: accentColor }}
            >
              <UserPlus className="w-5 h-5" />
              {t("card.createAccount")}
            </button>
            <button
              onClick={handleGoToLogin}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white border text-sm font-semibold hover:bg-gray-50 transition"
              style={{ borderColor: accentColor, color: accentColor }}
            >
              <LogIn className="w-5 h-5" />
              {t("card.iHaveAccount")}
            </button>
          </div>

          <p className="mt-6 text-xs text-gray-400">{appName}</p>
        </div>
      </div>
    );
  }

  // Claim mode — logged in user, unlinked card
  if (claimMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50">
              <CreditCard className="h-8 w-8 text-indigo-500" />
            </div>
            <h1 className="text-xl font-semibold text-gray-800 mb-1">{t("card.linkTitle")}</h1>
            <p className="text-sm text-gray-500">
              {t("card.linkSubtitle").replace("{code}", code.toUpperCase())}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            {/* Profile selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {profiles.length > 0 && !createNew ? t("card.linkTo") : t("card.nameYourProfile")}
              </label>

              {profiles.length > 0 && !createNew && (
                <div className="relative">
                  <select
                    value={selectedProfileId || ""}
                    onChange={(e) => setSelectedProfileId(parseInt(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none appearance-none bg-white pr-10"
                  >
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.profile_name} ({p.profile_type})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              )}

              {/* Create new profile input */}
              {createNew && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    placeholder={t("card.newProfileName")}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                    autoFocus
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "personal", label: t("card.typePersonal") },
                      { value: "business", label: t("card.typeBusiness") },
                      { value: "professional", label: t("card.typeProfessional") },
                    ].map((pt) => (
                      <button
                        key={pt.value}
                        type="button"
                        onClick={() => setNewProfileType(pt.value)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition ${
                          newProfileType === pt.value
                            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                            : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {pt.label}
                      </button>
                    ))}
                  </div>
                  {profiles.length > 0 && (
                    <button
                      onClick={() => {
                        setCreateNew(false);
                        if (!selectedProfileId && profiles.length > 0) {
                          setSelectedProfileId(profiles[0].id);
                        }
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      {t("card.useExisting")}
                    </button>
                  )}
                </div>
              )}

              {/* Create new button */}
              {!createNew && profiles.length > 0 && (
                <div className="mt-2">
                  {canCreateNew ? (
                    <button
                      onClick={() => {
                        setCreateNew(true);
                        setNewProfileName("");
                      }}
                      className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      <Plus className="w-4 h-4" />
                      {t("card.createNewProfile")}
                    </button>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">
                      {t("card.maxReached").replace("{max}", maxProfiles)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Selected profile preview */}
            {!createNew && selectedProfile && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {selectedProfile.profile_image ? (
                  <img
                    src={selectedProfile.profile_image}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                    {selectedProfile.profile_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {selectedProfile.profile_name}
                  </p>
                  <p className="text-xs text-gray-400 capitalize">
                    {selectedProfile.profile_type}
                  </p>
                </div>
              </div>
            )}

            {linkError && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">
                {linkError}
              </p>
            )}

            <button
              onClick={handleLink}
              disabled={linking || (!selectedProfileId && !createNew) || (createNew && !newProfileName.trim())}
              className="w-full rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {linking ? t("card.linking") : (createNew ? t("card.createAndLink") : t("card.confirmLink"))}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{t("card.errorTitle")}</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Loading
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent" />
    </div>
  );
}
